'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getMediaData, getFilmData, resizedImageProps } from '@/lib/utils/media.utils';
import type { Artworkvideo, Artworkimage } from '@/types';

interface MediaItem {
  media: Artworkvideo | Artworkimage;
  isVideo: boolean;
}

interface GalleryProps {
  videos?: Artworkvideo[];
  images?: Artworkimage[];
  className?: string;
}

const EMPTY_VIDEOS: Artworkvideo[] = [];
const EMPTY_IMAGES: Artworkimage[] = [];

export const Gallery = ({ videos = EMPTY_VIDEOS, images = EMPTY_IMAGES, className = '' }: GalleryProps) => {
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Tüm medya öğelerini birleştir
  const allMedia = useMemo(() => [
    ...videos.map(v => ({ media: v, isVideo: true })),
    ...images.map(i => ({ media: i, isVideo: false })),
  ], [videos, images]);

  const openGallery = (media: Artworkvideo | Artworkimage, isVideo: boolean) => {
    // Seçilen medyanın indeksini bul
    const index = allMedia.findIndex(item => item.media === media && item.isVideo === isVideo);

    setCurrentIndex(index);
    setSelectedMedia({ media, isVideo });
  };

  const closeGallery = () => {
    setSelectedMedia(null);
  };

  const goToNext = useCallback(() => {
    if (currentIndex < allMedia.length - 1) {
      const nextItem = allMedia[currentIndex + 1];
      setCurrentIndex(currentIndex + 1);
      setSelectedMedia(nextItem);
    }
  }, [currentIndex, allMedia]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      const prevItem = allMedia[currentIndex - 1];
      setCurrentIndex(currentIndex - 1);
      setSelectedMedia(prevItem);
    }
  }, [currentIndex, allMedia]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeGallery();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrev();
      }
    },
    [goToNext, goToPrev]
  );

  // Klavye olaylarını dinle
  useEffect(() => {
    if (selectedMedia) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [selectedMedia, handleKeyDown]);

  return (
    <>
      <div className={`grid grid-cols-3 gap-2 mt-5 sketch-grid ${className}`}>
        {videos.map((video, index) => (
          <div
            key={`video-${index}`}
            className="block aspect-square overflow-hidden cursor-pointer"
            onClick={() => openGallery(video, true)}
          >
            <video
              src={getFilmData(video).url}
              className="w-full h-full object-contain  transition-transform duration-300"
            />
          </div>
        ))}
        {images.map((image, index) => (
          <div
            key={`image-${index}`}
            className="block aspect-square overflow-hidden cursor-pointer"
            onClick={() => openGallery(image, false)}
          >
            <img
              {...resizedImageProps(getMediaData(image).url, 640, '(max-width: 768px) 50vw, 33vw')}
              alt={getMediaData(image).alt}
              className="w-full h-full object-contain  transition-transform duration-300"
            />
          </div>
        ))}
      </div>

      {/* Gelişmiş Galeri Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeGallery}
        >
          <div className="w-[90vw] h-[90vh] flex items-center justify-center relative" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            {selectedMedia.isVideo ? (
              <video
                src={getFilmData(selectedMedia.media).url}
                className="max-w-full max-h-[90vh] object-contain"
                controls
                autoPlay
              />
            ) : (
              <img
                src={getMediaData(selectedMedia.media).url}
                alt={getMediaData(selectedMedia.media).alt}
                className="max-w-full max-h-[90vh] object-contain"
              />
            )}

            {/* Close button */}
            <button
              className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 text-white transition-all"
              onClick={closeGallery}
              aria-label="Close"
            >
              <X size={24} />
            </button>

            {/* Navigation buttons */}
            {currentIndex > 0 && (
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 text-white transition-all"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  goToPrev();
                }}
                aria-label="Previous"
              >
                <ChevronLeft size={24} />
              </button>
            )}

            {currentIndex < allMedia.length - 1 && (
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 text-white transition-all"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  goToNext();
                }}
                aria-label="Next"
              >
                <ChevronRight size={24} />
              </button>
            )}

            {/* Media counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 px-3 py-1 rounded-full text-white text-[14px]">
              {currentIndex + 1} / {allMedia.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
